import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "@/lib/api";
import { useT } from "@/context/LanguageContext";
import { PageHero } from "@/components/common/PageHero";
import { CTASection } from "@/components/home/CTASection";
import { Clock, ArrowLeft } from "@phosphor-icons/react";

function renderContent(md) {
  return md.split("\n").map((line, i) => {
    const key = `${i}-${line.slice(0, 24)}`;
    if (line.startsWith("## ")) return <h2 key={key} className="mt-10 font-heading text-2xl font-semibold text-ink">{line.slice(3)}</h2>;
    if (line.startsWith("# ")) return <h1 key={key} className="mt-10 font-heading text-3xl font-bold text-ink">{line.slice(2)}</h1>;
    if (line.trim() === "") return <div key={key} className="h-2" />;
    return <p key={key} className="mt-4 text-base leading-relaxed text-slate-700">{line}</p>;
  });
}

export default function BlogPost() {
  const { slug } = useParams();
  const { lang } = useT();
  const [post, setPost] = useState(null);
  useEffect(() => { api.get(`/blog/${slug}`).then((r) => setPost(r.data)).catch(() => setPost(false)); }, [slug, lang]);

  if (post === false) return <div className="pt-40 pb-24 text-center text-slate-500">Article not found.</div>;
  if (!post) return <div className="pt-40 pb-24 text-center text-slate-400">Loading…</div>;

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: post.title,
    description: post.excerpt,
    image: post.cover_image,
    datePublished: post.date_published,
    author: { "@type": "Organization", name: post.author || "Unicorn Valley Group" },
    publisher: {
      "@type": "Organization",
      name: "Unicorn Valley Group",
      logo: { "@type": "ImageObject", url: "https://unicornvalleygroup.com/logo.png" },
    },
    mainEntityOfPage: { "@type": "WebPage", "@id": `https://unicornvalleygroup.com/blog/${post.slug}` },
  };

  return (
    <div data-testid="blog-post-page">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }} />
      <PageHero
        overline={post.category}
        title={post.title}
        subtitle={post.excerpt}
        crumbs={[{ label: "Trade Intelligence", to: "/blog" }, { label: post.category }]}
        image={post.cover_image}
      />
      <article className="bg-white py-16 lg:py-20">
        <div className="mx-auto max-w-3xl px-5 lg:px-8">
          <div className="flex items-center gap-4 border-b border-slate-100 pb-6 text-sm text-slate-500">
            <span>By {post.author}</span>
            <span className="flex items-center gap-1.5"><Clock size={15} /> {post.read_time}</span>
          </div>
          <img src={post.cover_image} alt={post.title} className="mt-8 h-80 w-full rounded-xl object-cover" />
          <div className="mt-8">{renderContent(post.content)}</div>
          <Link to="/blog" className="mt-12 inline-flex items-center gap-2 text-sm font-semibold text-teal hover:underline">
            <ArrowLeft size={16} weight="bold" /> Back to all articles
          </Link>
        </div>
      </article>
      <CTASection />
    </div>
  );
}
